import { getSGRWarrantyRequests } from 'lib/api/sgr';
import WarrantiesTable from 'lib/common/Tables/WarrantiesTable';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { generatePaginationQuery } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import { Pagination } from 'lib/types/shared';
import WarrantyDetailDialog from 'pages/Finance/Components/Financing/SGR/components/WarrantyDetailDialog';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
};

const SGR = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [openWarrantyDetail, setOpenWarrantyDetail] = useState(false);
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);
  const [selectedWarranty, setSelectedWarranty] = useState<Warranty | null>(
    null,
  );

  const handleOpenWarrantyDetail = (warrantyId: number) => {
    const warranty = warranties.find((warranty) => warranty.id === warrantyId);
    if (!warranty) return;
    setSelectedWarranty(warranty);
    setOpenWarrantyDetail(true);
  };

  const fetchWarranties = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      if (id === undefined || id === null || id.trim() === '') return;
      setIsLoading(true);
      const query = generatePaginationQuery({
        page,
        pageSize,
        businessIdentifier: id,
      });
      const response = await getSGRWarrantyRequests(dispatch, query);

      const paginationResponse = {
        page: response.pagination.page,
        pageSize: response.pagination.pageSize,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      };
      setWarranties(response.data);
      setPagination(paginationResponse);
      return response.data;
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshWarranties = async (params?: {
    page: number;
    pageSize: number;
  }) => {
    const response = await fetchWarranties({
      page: params?.page ?? pagination.page,
      pageSize: params?.pageSize ?? pagination.pageSize,
    });
    if (response && selectedWarranty) {
      const updatedWarranty = response.find(
        (w: Warranty) => w.id === selectedWarranty.id,
      );
      if (updatedWarranty) setSelectedWarranty(updatedWarranty);
    }
  };

  useEffect(() => {
    void fetchWarranties({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
  }, [pagination.page, pagination.pageSize]);

  if (isLoading) return <CenteredCircularProgress />;

  return (
    <>
      <WarrantiesTable
        warranties={warranties}
        setWarranties={setWarranties}
        handleOpenWarrantyDetail={handleOpenWarrantyDetail}
        pagination={pagination}
        setPagination={setPagination}
        loading={isLoading}
        fetchWarranties={handleRefreshWarranties}
        setIsLoading={setIsLoading}
        showBusiness={false}
      />
      <WarrantyDetailDialog
        open={openWarrantyDetail}
        onClose={() => setOpenWarrantyDetail(false)}
        warranty={selectedWarranty ?? ({} as Warranty)}
        refreshWarranty={() => handleRefreshWarranties}
      />
    </>
  );
};

export default SGR;
